// This file is automatically generated. Do not edit it directly.
//
// SECURITY NOTE: The values below are the Supabase "anon" (publishable) key and project URL.
// These are designed to be public — they are safe to include in client-side code.
// All data access is protected by Row Level Security (RLS) policies on every table.
// See: https://supabase.com/docs/guides/auth/row-level-security
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { getSafeLocalStorage, getSafeSessionStorage } from '@/utils/safeStorage';

export const SUPABASE_URL = "https://vjsygjwemdjrremspuco.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZqc3lnandlbWRqcnJlbXNwdWNvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwNTUzNTYsImV4cCI6MjA4MjYzMTM1Nn0.A96pQeo5OvRK72XB5b-3SjeFp-ImyZN342dX6d3iTLg";

interface SupabaseClientConfigInput {
  readonly mode: string;
  readonly localUrl?: string;
  readonly localPublishableKey?: string;
}

interface SupabaseClientConfig {
  readonly url: string;
  readonly publishableKey: string;
  readonly usesLocalOverride: boolean;
}

export function resolveSupabaseClientConfig({
  mode,
  localUrl,
  localPublishableKey,
}: SupabaseClientConfigInput): SupabaseClientConfig {
  if (
    mode !== "production" &&
    localUrl &&
    localPublishableKey?.trim()
  ) {
    try {
      const parsedUrl = new URL(localUrl);
      const isHttp = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      const isLoopback = parsedUrl.hostname === "localhost" || parsedUrl.hostname === "127.0.0.1";
      if (isHttp && isLoopback) {
        return {
          url: localUrl,
          publishableKey: localPublishableKey,
          usesLocalOverride: true,
        };
      }
    } catch {
      // Invalid or non-loopback overrides fall through to production constants.
    }
  }

  return {
    url: SUPABASE_URL,
    publishableKey: SUPABASE_PUBLISHABLE_KEY,
    usesLocalOverride: false,
  };
}

const supabaseClientConfig = resolveSupabaseClientConfig({
  mode: import.meta.env.MODE,
  localUrl: import.meta.env.VITE_LOCAL_SUPABASE_URL,
  localPublishableKey: import.meta.env.VITE_LOCAL_SUPABASE_PUBLISHABLE_KEY,
});

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

const SUPABASE_AUTH_STORAGE_KEY = "sb-vjsygjwemdjrremspuco-auth-token";
const safeStorage = getSafeLocalStorage();
const previousSessionStorage = getSafeSessionStorage();

const migrateSessionStorageAuthToken = () => {
  const existingLocalToken = safeStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
  if (existingLocalToken) {
    return;
  }

  const existingSessionToken = previousSessionStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
  if (existingSessionToken) {
    safeStorage.setItem(SUPABASE_AUTH_STORAGE_KEY, existingSessionToken);
  }
};

migrateSessionStorageAuthToken();

const _supabase = createClient(supabaseClientConfig.url, supabaseClientConfig.publishableKey, {
  auth: {
    storageKey: SUPABASE_AUTH_STORAGE_KEY,
    storage: safeStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});

export const supabase: SupabaseClient<Database> = _supabase;
